import { useEffect, useState } from "react";
import { Download, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  downloadFinalInvoicePdf,
  sendFinalInvoiceToClient,
  viewFinalInvoicePdf,
  type ProjectFinalInvoiceSummary,
} from "@/lib/projects-clients";
import { formatCurrency } from "@/lib/tickets";
import { useToast } from "@/hooks/use-toast";

type FinalInvoicePreviewDialogProps = {
  projectId: string | null;
  projectName?: string | null;
  clientName?: string | null;
  clientEmail?: string | null;
  invoice: ProjectFinalInvoiceSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: (invoice: ProjectFinalInvoiceSummary) => void;
};

function amountDueLabel(invoice: ProjectFinalInvoiceSummary) {
  if (invoice.balanceDue < 0) {
    return `Overpaid ${formatCurrency(Math.abs(invoice.balanceDue))}`;
  }
  if (Math.abs(invoice.balanceDue) < 0.005) {
    return "Paid in full";
  }
  return `Balance due ${formatCurrency(invoice.balanceDue)}`;
}

export function FinalInvoicePreviewDialog({
  projectId,
  projectName,
  clientName,
  clientEmail,
  invoice,
  open,
  onOpenChange,
  onSent,
}: FinalInvoicePreviewDialogProps) {
  const { toast } = useToast();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState("final-invoice.pdf");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectId || !invoice) {
      setPdfUrl(null);
      return;
    }

    let canceled = false;
    let objectUrl: string | null = null;
    setLoadingPdf(true);
    setError(null);
    setPdfUrl(null);

    viewFinalInvoicePdf(projectId, invoice.id)
      .then(({ blob, fileName }) => {
        if (canceled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
        setPdfFileName(fileName);
      })
      .catch((err) => {
        if (canceled) return;
        setError(
          err instanceof Error
            ? err.message
            : "Could not load invoice preview.",
        );
      })
      .finally(() => {
        if (!canceled) setLoadingPdf(false);
      });

    return () => {
      canceled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoice, open, projectId]);

  const handleDownload = async () => {
    if (!projectId || !invoice) return;
    setDownloading(true);
    try {
      const { blob, fileName } = await downloadFinalInvoicePdf(
        projectId,
        invoice.id,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName || pdfFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Could not download final invoice",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleSend = async () => {
    if (!projectId || !invoice || !clientEmail?.trim()) return;
    setSending(true);
    try {
      const updated = await sendFinalInvoiceToClient(projectId, invoice.id);
      toast({
        title: "Final invoice sent",
        description: `Sent to ${updated.sentToEmail ?? clientEmail}.`,
      });
      onSent?.(updated);
    } catch (err) {
      toast({
        title: "Could not send final invoice",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const sendDisabled =
    !clientEmail?.trim() || sending || !projectId || !invoice;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => !sending && onOpenChange(nextOpen)}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-[#0C1F4F]">
            Final Invoice Generated
          </DialogTitle>
          <DialogDescription>
            Preview the generated invoice PDF and send it to the client when
            ready.
          </DialogDescription>
        </DialogHeader>

        {invoice ? (
          <div className="space-y-4">
            <div className="grid gap-3 rounded-lg border bg-muted/20 p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <p>
                <span className="block text-xs text-muted-foreground">
                  Invoice number
                </span>
                <span className="font-semibold text-[#0C1F4F]">
                  {invoice.invoiceNumber}
                </span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Project
                </span>
                <span className="font-medium">{projectName || "Project"}</span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Client
                </span>
                <span className="font-medium">{clientName || "Client"}</span>
              </p>
              <p>
                <span className="block text-xs text-muted-foreground">
                  Approved total
                </span>
                <span className="font-semibold text-[#0C1F4F]">
                  {formatCurrency(invoice.approvedTotal)}
                </span>
              </p>
              {invoice.showPayments !== false ? (
                <>
                  <p>
                    <span className="block text-xs text-muted-foreground">
                      Payments received
                    </span>
                    <span className="font-medium">
                      {formatCurrency(invoice.paymentsReceived)}
                    </span>
                  </p>
                  <p>
                    <span className="block text-xs text-muted-foreground">
                      Balance
                    </span>
                    <span className="font-semibold text-accent">
                      {amountDueLabel(invoice)}
                    </span>
                  </p>
                </>
              ) : null}
              {invoice.sentAt ? (
                <p className="sm:col-span-2">
                  <span className="block text-xs text-muted-foreground">
                    Sent
                  </span>
                  <span className="font-medium">
                    {new Date(invoice.sentAt).toLocaleString()} ·{" "}
                    {invoice.sentToEmail}
                  </span>
                </p>
              ) : null}
            </div>

            {!clientEmail?.trim() ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Client email is missing. Add an email to the client before
                sending the invoice.
              </div>
            ) : null}

            <div className="min-h-[520px] overflow-hidden rounded-lg border bg-muted/20">
              {loadingPdf ? (
                <div className="flex h-[520px] items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading invoice preview...
                </div>
              ) : error ? (
                <div className="flex h-[520px] flex-col items-center justify-center gap-2 p-4 text-center text-sm">
                  <p className="font-medium text-destructive">
                    PDF preview is unavailable.
                  </p>
                  <p className="max-w-md text-muted-foreground">
                    {error} You can download the invoice PDF instead.
                  </p>
                </div>
              ) : pdfUrl ? (
                <iframe
                  title="Final invoice PDF preview"
                  src={pdfUrl}
                  className="h-[70vh] min-h-[520px] w-full"
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Close
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => void handleDownload()}
            disabled={!invoice || downloading}
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download PDF
          </Button>
          <Button
            className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
            onClick={() => void handleSend()}
            disabled={sendDisabled}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {invoice?.status === "SENT" ? "Resend to client" : "Send to client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
